import { useMutation } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import type { OrpcClient } from '@/orpc/client'
import { useOrpc } from './useClient'

export type SetPractitionerBridgeProviderInput = Parameters<
  OrpcClient['setPractitionerBridgeProvider']
>[0]

/**
 * Links or unlinks a member and a Bridge provider. Without the link Bridge's eligibility
 * answers name nobody we recognise, so the notifications say what the mapping enables
 * rather than reporting a generic save.
 *
 * The middleware checks the provider against Bridge — including that its NPI matches this
 * member's — before persisting; its rejection messages are written to be shown as-is.
 *
 * `externalIdSynced: false` is worth surfacing but is not a failure: the mapping is saved
 * and works, only Bridge's mirror of it is missing, and saving again repairs it.
 */
export function useSetPractitionerBridgeProvider() {
  const orpc = useOrpc()

  return useMutation(
    orpc.setPractitionerBridgeProvider.mutationOptions({
      onSuccess: (result) => {
        if (!result.bridgeProviderId) {
          showNotification({
            color: 'green',
            title: 'Bridge provider unlinked',
            message: 'This member is no longer mapped to a Bridge provider.',
          })
          return
        }
        showNotification({
          color: result.externalIdSynced ? 'green' : 'yellow',
          title: 'Bridge provider linked',
          message: result.externalIdSynced
            ? `Mapped to Bridge provider ${result.bridgeProviderId}.`
            : `Mapped to Bridge provider ${result.bridgeProviderId}, but Bridge could not be updated with our id. The mapping works; save again to retry.`,
        })
      },
      onError: (error) => {
        showNotification({
          color: 'red',
          title: 'Bridge mapping not saved',
          message: error instanceof Error ? error.message : 'Unknown error',
        })
      },
    }),
  )
}
